
import { useState, useEffect } from 'react';
import { getAudienceSize, calculateAudienceSizeV2, calculateAudienceSizeTest, AudienceSizeResponse } from '@/services/smsApi';
import { useDeepCompareEffect } from '@/hooks/useDeepCompareEffect';

interface AudienceFilters {
  audienceType?: string;
  filters?: {
    totalOrders?: {
      lowerLimit: number | null;
      upperLimit: number | null;
    } | null;
    lastVisitDate?: {
      fromDate: Date | null;
      toDate: Date | null;
    } | null;
    averageOrderValue?: {
      lowerLimit: number | null;
      upperLimit: number | null;
    } | null;
    totalSpend?: {
      lowerLimit: number | null;
      upperLimit: number | null;
    } | null;
  };
}

export const useSmsAudience = (
  selectedRestaurants: string[] = [],
  filters: AudienceFilters = {},
  shouldFetch: boolean = false,
  userRestaurants: any[] = [] // Added userRestaurants parameter
) => {
  const [audience, setAudience] = useState<any[]>([]);
  const [audienceSize, setAudienceSize] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAudienceSize = async () => {
      // Debug logging
      console.log('[SMS-DEBUG] fetchAudienceSize triggered with state:', {
        shouldFetch,
        selectedRestaurantsLength: selectedRestaurants?.length || 0,
        selectedRestaurantsContent: selectedRestaurants,
        filtersType: filters?.audienceType,
        hasUserRestaurants: Array.isArray(userRestaurants) && userRestaurants.length > 0,
        userRestaurantsCount: userRestaurants?.length || 0
      });
      
      // Skip if we shouldn't fetch or if no restaurants are selected
      if (!shouldFetch) {
        console.log('[SMS-DEBUG] Skipping audience fetch - shouldFetch is false');
        return;
      }
      
      // Don't make the API call if there are no restaurants, but use a fallback size
      if (selectedRestaurants.length === 0) {
        console.log('[SMS-DEBUG] No restaurants selected - using fallback size');
        const fallbackSize = filters.audienceType === 'all' ? 150 : 75;
        setAudienceSize(fallbackSize);
        setAudience([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Debug log the user restaurants data
        console.log('[SMS-DEBUG] User restaurants available:', {
          count: userRestaurants?.length || 0,
          ids: userRestaurants?.map(r => r.id) || [],
          firstRestaurant: userRestaurants?.length > 0 ? {
            id: userRestaurants[0].id,
            name: userRestaurants[0].name
          } : 'No restaurants'
        });
        
        // Convert 'all' to actual restaurant IDs instead of sending an empty array
        const hasAll = selectedRestaurants.includes('all');
        const availableRestaurantIds = userRestaurants?.map(r => r.id) || [];
        
        console.log('[SMS-DEBUG] Converting restaurant selection:', {
          originalSelection: [...selectedRestaurants],
          hasAll,
          availableIdsCount: availableRestaurantIds.length,
          includesAll: selectedRestaurants.includes('all')
        });
        
        const restaurantIds = hasAll
          ? availableRestaurantIds // Send actual restaurant IDs
          : selectedRestaurants;
          
        console.log('[SMS-DEBUG] Final restaurantIds being sent:', restaurantIds);
        
        // Ensure we pass valid filters - prevent undefined values
        const safeFilters = {
          audienceType: filters.audienceType || 'all',
          filters: {
            // Only new filter formats
            totalOrders: filters.filters?.totalOrders || null,
            lastVisitDate: filters.filters?.lastVisitDate || null,
            averageOrderValue: filters.filters?.averageOrderValue || null,
            totalSpend: filters.filters?.totalSpend || null
          }
        };
        
        console.log('[SMS-DEBUG] Calling getAudienceSize with params:', {
          restaurantIds,
          filters: safeFilters
        });
        
        try {
          // Try the primary endpoint first
          const response = await getAudienceSize(restaurantIds, safeFilters);
          console.log('[SMS-DEBUG] getAudienceSize primary success:', response);
          
          setAudienceSize(response.size || 0);
          setAudience(Array.isArray(response.sample) ? response.sample : []);
          
        } catch (primaryError) {
          console.warn('[SMS-DEBUG] Primary endpoint failed, trying V2:', primaryError);
          
          try {
            // Try the V2 endpoint as first fallback
            const v2Response = await calculateAudienceSizeV2(restaurantIds, safeFilters);
            console.log('[SMS-DEBUG] V2 endpoint success:', v2Response);
            
            setAudienceSize(v2Response.size || 0);
            setAudience(Array.isArray(v2Response.sample) ? v2Response.sample : []);
            
          } catch (v2Error) {
            console.warn('[SMS-DEBUG] V2 endpoint also failed, trying test endpoint:', v2Error);
            
            // Try the test endpoint as final fallback
            const testResponse = await calculateAudienceSizeTest(restaurantIds, safeFilters);
            console.log('[SMS-DEBUG] Using test data as fallback:', testResponse);
            
            setAudienceSize(testResponse.size || 0);
            setAudience(Array.isArray(testResponse.sample) ? testResponse.sample : []);
          }
        }
      } catch (err) {
        console.error('Error fetching audience size:', err);
        setError('Failed to fetch audience data');
        // Set a fallback size based on filters
        const fallbackSize = filters.audienceType === 'all' ? 150 : 75;
        setAudienceSize(fallbackSize);
        setAudience([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAudienceSize();
  // Use deep comparison for filters to prevent unnecessary re-renders
  }, [selectedRestaurants, shouldFetch, userRestaurants, JSON.stringify(filters)]);

  return {
    audience,
    audienceSize,
    loading,
    error
  };
};
